import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  DeleteConfirmation,
  Loader,
  SectionHeader,
} from '../../common/components';
import {
  deleteSkiResortAsync,
  getSkiResortDetailsAsync,
  saveSkiResortAsync,
  selectSkiResortDetails,
  selectSkiResortDetailsLoadingStatus,
  SkiResortDetails,
  updateSkiResortAsync,
} from './skiResortsSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS } from '../../common/constants';
import { initialTranslationsState } from '../../common/types/translations.types';
import { getRegionsAsync, selectRegionItems } from '../regions/regionsSlice';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';
import { ensureTranslationItems } from '../../common/utils/translationUtils';

function SkiResortAddOrUpdate() {
  const backLinkUrl = '/locations/skiresorts';
  const params = useParams();
  const dispatch = useAppDispatch();
  const skiResortDetails = useAppSelector(selectSkiResortDetails);
  const regions = useAppSelector(selectRegionItems);
  const skiResortDetailsLoadingStatus = useAppSelector(
    selectSkiResortDetailsLoadingStatus,
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const isAddMode = !params.skiResortId;

  const initialSkiResortState: SkiResortDetails = {
    nameTranslations: initialTranslationsState,
    descriptionTranslations: initialTranslationsState,
    metaTitleTranslations: ensureTranslationItems([], 'SkiResortMetaTitle'),
    metaDescriptionTranslations: ensureTranslationItems(
      [],
      'SkiResortMetaDescription',
    ),
    latitude: '',
    longitude: '',
    regionId: '',
  };

  const [skiResort, setSkiResort] = useState(initialSkiResortState);
  const [skiResortNameFi, setSkiResortNameFi] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleted, setDeleted] = useState(false);

  const regionOptions = regions.map(region => {
    return { value: region.id, label: region.name };
  });

  const initFetch = useCallback(() => {
    dispatch(getSkiResortDetailsAsync(params.skiResortId));
    dispatch(
      getRegionsAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
      }),
    );
  }, [dispatch, params.skiResortId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (!isAddMode) {
      initFetch();
    }
  }, [isAddMode, initFetch]);

  useEffect(() => {
    if (!isAddMode) {
      setSkiResort({
        ...skiResortDetails,
        metaTitleTranslations: ensureTranslationItems(
          skiResortDetails.metaTitleTranslations,
          'SkiResortMetaTitle',
        ),
        metaDescriptionTranslations: ensureTranslationItems(
          skiResortDetails.metaDescriptionTranslations,
          'SkiResortMetaDescription',
        ),
      });
    }
  }, [isAddMode, skiResortDetails]);

  useEffect(() => {
    if (skiResort.nameTranslations.length > 0) {
      const skiResortObject = skiResort.nameTranslations.find(
        obj => obj.locale === 'fi_FI',
      );
      setSkiResortNameFi(skiResortObject.text);
    }
  }, [skiResort]);

  const deleteSkiResort = () => {
    dispatch(deleteSkiResortAsync(params.skiResortId));
    setDeleted(true);
    setShowDeleteModal(false);
  };

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newSkiResort = { ...skiResort };
    let pointer = newSkiResort;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setSkiResort(newSkiResort);
  };

  const handleTranslation = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { culture } = event.target.dataset;
    const textValue = event.target.value;
    const newSkiResort = { ...skiResort };

    newSkiResort[finalProp] = skiResort[finalProp].map(item => {
      if (item.locale === culture) {
        return { ...item, text: textValue };
      }
      return item;
    });
    setSkiResort(newSkiResort);
  };

  const handleSelectChange = (selectedOption, name) => {
    const newSkiResort = { ...skiResort };
    newSkiResort[name] = selectedOption.value;
    setSkiResort(newSkiResort);
  };

  const addOrUpdateSkiResort = async () => {
    let actionResult: Action<string>;
    if (isAddMode) {
      actionResult = await dispatch(saveSkiResortAsync(skiResort));
    } else {
      actionResult = await dispatch(updateSkiResortAsync(skiResort));
    }

    if (
      saveSkiResortAsync.fulfilled.match(actionResult) ||
      updateSkiResortAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Maan ${skiResortNameFi} tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const generatePageTitle = function () {
    if (isAddMode) {
      return 'Uuden hiihtokeskuksen lisäys';
    }
    if (
      skiResortDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      skiResortNameFi !== ''
    ) {
      return `Hiihtokeskuksen tietojen päivitys: ${skiResortNameFi}`;
    }
    return 'Hiihtokeskuksen tietojen päivitys';
  };

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Hiihtokeskukset</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Sijaintitiedot', 'Hiihtokeskukset']}
          Title={generatePageTitle()}
        />

        <Loader
          IsLoading={skiResortDetailsLoadingStatus === LOADING_STATUS.LOADING}
        />

        {skiResortDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            {skiResort.nameTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="nameTranslations"
                className="mb-40"
              >
                <Form.Label>Nimi ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="nameTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Form.Group className="mb-40">
              <Form.Label>Alue</Form.Label>
              <Select
                inputId="regionId"
                name="regionId"
                placeholder="Valitse alue"
                isSearchable
                options={regionOptions}
                onChange={option => handleSelectChange(option, 'regionId')}
                value={regionOptions.filter(
                  option => option.value === skiResort.regionId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <hr className="hr-light mb-32" />

            {skiResort.descriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="descriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Kuvaus ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="descriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {skiResort.metaTitleTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaTitleTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-otsikko ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaTitleTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {skiResort.metaDescriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaDescriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-kuvausteksti ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaDescriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Row xs={2}>
              <Col>
                <Form.Group controlId="latitude">
                  <Form.Label>Leveysaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={skiResort.latitude}
                    onChange={handleInputChange}
                    name="latitude"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="longitude">
                  <Form.Label>Pituusaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={skiResort.longitude}
                    onChange={handleInputChange}
                    name="longitude"
                  />
                </Form.Group>
              </Col>
            </Row>

            <InputErrorsDisplay inputErrors={inputErrors} />

            <ActionButtons
              CancelButtonText={
                deleted || submitted ? 'Palaa takaisin' : 'Peruuta'
              }
              CancelButtonUrl={backLinkUrl}
              DeleteButtonText={
                isAddMode || deleted ? '' : 'Poista hiihtokeskus'
              }
              HandleDelete={
                isAddMode || deleted ? null : () => setShowDeleteModal(true)
              }
              HandleSave={addOrUpdateSkiResort}
            />

            {submitted && <Alert variant="success">Tiedot tallennettu!</Alert>}
            {(skiResort.isDeleted || deleted) && (
              <Alert variant="danger">Hiihtokeskus on poistettu!</Alert>
            )}
          </Container>
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={deleteSkiResort}
        HideModal={() => setShowDeleteModal(false)}
        Id={skiResort.id}
        Message={`Haluatko varmasti poistaa hiihtokeskuksen ${skiResortNameFi}?`}
        ShowModal={showDeleteModal}
      />
    </>
  );
}

export default SkiResortAddOrUpdate;
